/*
 * Copyright 2025 Intuition Robotics ltd
 */
import type {DB_Object} from "@intuitionrobotics/firebase";
import type {ApiWithBody} from "@intuitionrobotics/thunderstorm";
import type {AuditBy} from "@intuitionrobotics/ts-common";
import type {MessageType} from "@intuitionrobotics/push-pub-sub";
import type {DB_Organization} from "@app/ir-q-app-common/types/care-center/organization";
import { ORGANIZATION_TYPE} from "@app/ir-q-app-common/types/care-center/organization";
import {windowType} from "./api.js";

export type DB_PatientConfigFull = DB_Object & {
	patientId: string
	config: CareConfig
	_updated: AuditBy
	_created: AuditBy
}

export enum ConfigType {
	SELF   = "self",
	DOCTOR = "doctor"
}

export type ConfigKey = typeof allowedConfigElements[number];

export type DB_PatientConfig = DB_Object & CareFieldVal & {
	patientId: string
	configKey: ConfigKey
	type: ConfigType
	_updated: AuditBy
	_created: AuditBy
}

export type CareConfig = {
	[key: string]: CareFieldVal | undefined
}

export const getNewCareField = (name: string, newCareElement: CareFieldVal) => {
	const { frequency } = newCareElement;
	const windowTypeValue = windowType[timeUnits.indexOf(frequency.value)];

	return {
		name,
		windowType: windowTypeValue,
		threshold: frequency.times,
		enabled: newCareElement.enabled,
		ranges: newCareElement.ranges || {}
	};
};
// Deleted configs
export const INTRO_CONFIG_KEY = "medication_onboarding";
export const PRESCRIPTION_PICKUP_CONFIG_KEY = "prescription_pickup";
export const MEDICATION_STARTED_CONFIG_KEY = "medication_started";
export const MEDICATION_TRACKING_CONFIG_KEY = "medication_tracking";
export const SIDE_EFFECTS_CONFIG_KEY = "side_effects";
export const PRESCRIBED_MEDICATION_INTAKE_CONFIG_KEY = "medication_adherence";
export const MEDICATION_SUPPLY_CONFIG_KEY = "medication_supply_status";

export const deletedElements = [
	INTRO_CONFIG_KEY,
	PRESCRIPTION_PICKUP_CONFIG_KEY,
	MEDICATION_STARTED_CONFIG_KEY,
	MEDICATION_TRACKING_CONFIG_KEY,
	SIDE_EFFECTS_CONFIG_KEY,
	PRESCRIBED_MEDICATION_INTAKE_CONFIG_KEY,
	MEDICATION_SUPPLY_CONFIG_KEY
];


// Checkin elements
export const PAIN_LEVELS_CONFIG_KEY = "pain_assessment";
export const ENERGY_LEVELS_CONFIG_KEY = "energy_check";
export const GENERAL_WELL_BEING_CONFIG_KEY = "general_wellbeing";

// For migrations.
export const PHYSICAL_EXERCISE_CONFIG_KEY = "physical_exercise";
export const STRESS_REDUCTION_CONFIG_KEY = "stress_reduction";

// Health measurements elements
export const BLOOD_PRESSURE_CONFIG_KEY = "blood_pressure";
export const WEIGHT_CONFIG_KEY = "weight";
export const BLOOD_GLUCOSE_CONFIG_KEY = "blood_glucose";
export const BLOOD_OXYGEN_CONFIG_KEY = "blood_oxygen";
export const HEART_RATE_CONFIG_KEY = "heart_rate";

// Care checkin
export enum CheckinConfigKey {
	ENERGY_CHECK             = "energy_check",
	GENERAL_WELLBEING        = "general_wellbeing",
	PAIN_ASSESSMENT          = "pain_assessment",
}

export const checkinElements = Object.values(CheckinConfigKey) as string[];

// Health data self report
export enum HealthMeasurementsConfigKey {
	BLOOD_GLUCOSE  = "blood_glucose",
	BLOOD_OXYGEN   = "blood_oxygen",
	BLOOD_PRESSURE = "blood_pressure",
	HEART_RATE     = "heart_rate",
	WEIGHT         = "weight",
}

export enum BloodGlucoseSubConfigKey {
	BEFORE_BREAKFAST = "before_breakfast",
	AFTER_BREAKFAST  = "after_breakfast",
	BEFORE_LUNCH     = "before_lunch",
	AFTER_LUNCH      = "after_lunch",
	BEFORE_DINNER    = "before_dinner",
	AFTER_DINNER     = "after_dinner",
}

export enum BloodPressureSubConfigKey {
	SYSTOLIC  = "systolic",
	DIASTOLIC = "diastolic",
}

export const measurementsElements = Object.values(HealthMeasurementsConfigKey) as string[];

// Wellness Coach
export enum WellnessCoachConfigKey {
	INCREASE_PHYSICAL_ACTIVITY = "increase_physical_activity",
	STAY_SHARP                 = "stay_sharp",
	INCREASE_RELAXATION        = "increase_relaxation",
	IMPROVE_SLEEP              = "improve_sleep",
	HYDRATION_REMINDER         = "hydration_reminder"
}

export const wellnessCoachElements = Object.values(WellnessCoachConfigKey) as string[];

export const allowedConfigElements = [
	...checkinElements,
	...measurementsElements,
	...wellnessCoachElements
] as string[];

export type MeasurementRange = {
	[key: string]: {
		min: number,
		max: number
	} | undefined
}

export enum CareFieldImportance {
	LOW    = "Low",
	MEDIUM = "Medium",
	HIGH   = "High"
}

export enum UserCategory {
	CLINICAL     = "CLINICAL",
	NON_CLINICAL = "NON_CLINICAL",
}

export type DB_Range = {
	ranges: MeasurementRange
	goalName: string
	unitId: string
}

export type CareFieldVal = {
	ranges?: MeasurementRange
	enabled: boolean
	importance: CareFieldImportance
	frequency: Frequency
	dateTimes: DateTimes
	ttl?: number
}

export type Frequency = {
	type: string
	times: number
	value: TimeUnit
}

export type DateTimes = {
	timesArray: TimeRange[]
	daysArray: string[]
	monthsArray: string[]
};

export const defaultDateTimes = (): DateTimes => ({
	timesArray: [],
	daysArray: [],
	monthsArray: []
});

export type TimeRange = {
	timeFrom: string
	timeEnd: string
}

export const DefaultFrequencyType = "unit";
export type TimeUnit = typeof timeUnits[number]
export const DefaultFiveTimesDay: Frequency = {value: "day", times: 5, type: DefaultFrequencyType};
export const DefaultEveryDay: Frequency = {value: "day", times: 1, type: DefaultFrequencyType};
export const DefaultTwiceDay: Frequency = {value: "day", times: 2, type: DefaultFrequencyType};
export const DefaultTriceDay: Frequency = {value: "day", times: 3, type: DefaultFrequencyType};
export const DefaultEveryWeek: Frequency = {value: "week", times: 1, type: DefaultFrequencyType};
export const DefaultThreeTimesAWeek: Frequency = {value: "week", times: 3, type: DefaultFrequencyType};
export const DefaultEveryMonth: Frequency = {value: "month", times: 1, type: DefaultFrequencyType};
export const DefaultEveryOtherDay: Frequency = {value: "day", times: 0.5, type: DefaultFrequencyType};
export const DefaultFieldImportance: CareFieldImportance = CareFieldImportance.MEDIUM;
export const timeUnits = <const>["day",
                                 "week",
                                 "month"];
export const DefaultTwiceAWeek: Frequency = {value: "week", times: 2, type: DefaultFrequencyType};


export const frequencyCareElement = (frequency: Frequency): CareFieldVal => ({
	enabled: false,
	frequency,
	dateTimes: defaultDateTimes(),
	importance: DefaultFieldImportance
});

export const DefaultDailyCareElement = frequencyCareElement(DefaultEveryDay);

export type Request_UMUpdate = {
	user?: string
	configurationType: string
	configurations: { [p: string]: CareFieldVal & { userCategory: UserCategory } | undefined }
	shouldDeleteExisting?: boolean
}
export type Response_UMUpdate = {}
export type UserModelApi_Update = ApiWithBody<"/usermodel/configurations/:unitId", Request_UMUpdate, Response_UMUpdate>;

type FrequencyNames = "WEEKLY" | "MONTHLY";

export const Frequencies: { [p in FrequencyNames]: Frequency } = {
	WEEKLY: DefaultEveryWeek,
	MONTHLY: DefaultEveryMonth
};

export const CareConfigChange = "care-config-change";

export type MessageType_ConfigChange = MessageType<typeof CareConfigChange, { organizationId: string }, { doc: DB_PatientConfig }>
export const PatientCohortChange = "patient-cohort-change";

export type SubKey = { measurement: string, defaultRange?: { min: number, max: number } };

export type SubKeys = {
	[key: string]: SubKey
};

export type CareFieldDef = {
	key: string
	presentation: string
	shortDescription?: string[]
	description?: string
	units: TimeUnit[]
	default: Frequency
	options: Frequency[]
	subKeys?: SubKeys
	type: "frequency"
	show: boolean
	toggle: boolean
	timeUnits?: TimeUnit[]
}

type CheckinFieldDef = CareFieldDef & { key: CheckinConfigKey };

type CheckinFieldDefs = Record<CheckinConfigKey, CheckinFieldDef>;
export const checkinFieldDefsForOrganization = (organization?: DB_Organization): CheckinFieldDefs => {
	const isClinical = organization?.type === ORGANIZATION_TYPE.CLINICAL;
	return {
		[CheckinConfigKey.PAIN_ASSESSMENT]: {
			key: CheckinConfigKey.PAIN_ASSESSMENT,
			presentation: "Track pain levels",
			shortDescription: ["ElliQ will ask the customer if they are experiencing any unusual pain"],
			type: "frequency",
			options: [
				DefaultEveryDay,
				DefaultEveryWeek,
				DefaultEveryMonth
			],
			units: ["week",
				"month"],
			show: true,
			default: isClinical ? DefaultEveryWeek: DefaultEveryMonth,
			toggle: true
		},
		[CheckinConfigKey.ENERGY_CHECK]: {
			key: CheckinConfigKey.ENERGY_CHECK,
			presentation: "Track energy levels",
            shortDescription: ["ElliQ will ask the customer to rate their energy level, and track any changes"],
			type: "frequency",
			options: [
				DefaultEveryDay,
				DefaultEveryWeek,
				DefaultEveryMonth
			],
			units: ["week", "month"],
			show: true,
			default: isClinical ? DefaultEveryWeek: DefaultEveryMonth,
			toggle: true
		},
		[CheckinConfigKey.GENERAL_WELLBEING]: {
			key: CheckinConfigKey.GENERAL_WELLBEING,
			presentation: "Track general wellbeing",
			shortDescription: ["ElliQ will ask the customer how they are generally feeling"],
			type: "frequency",
			options: [
				DefaultEveryDay,
				DefaultEveryWeek,
				DefaultEveryMonth
			],
			units: ["week",
				"month"],
			default: DefaultEveryWeek,
			show: isClinical,
			toggle: true
		}
	}
};

export const checkinFieldDefsArrayForOrganization = (organization?: DB_Organization): CheckinFieldDef[] => {
	return Object.values(checkinFieldDefsForOrganization(organization));
};

export const Key_Before_Breakfast = "before_breakfast";
export const Key_After_Breakfast = "after_breakfast";
export const Key_Before_Lunch = "before_lunch";
export const Key_After_Lunch = "after_lunch";
export const Key_Before_Dinner = "before_dinner";
export const Key_After_Dinner = "after_dinner";

type MeasurementFieldDef = CareFieldDef & { key: HealthMeasurementsConfigKey };

type MeasurementFieldDefs = Record<HealthMeasurementsConfigKey, MeasurementFieldDef>;
// Original defaults. Override per usage.

// export const StaticMeasurementFieldDefs: MeasurementFieldDefs = {

// Same for all orgs
export const measurementFieldDefsForOrganization = (): MeasurementFieldDefs => {
	return {
		[HealthMeasurementsConfigKey.BLOOD_PRESSURE]: {
			key: HealthMeasurementsConfigKey.BLOOD_PRESSURE,
			presentation: "Blood pressure",
			shortDescription: ["ElliQ will ask the customer for their blood pressure measurement"],
			type: "frequency",
			options: [
				DefaultEveryDay,
				DefaultEveryWeek,
				DefaultTwiceAWeek
			],
			units: [],
			subKeys: {
				[BloodPressureSubConfigKey.SYSTOLIC]: {measurement: "mmHg", defaultRange: {min: 90, max: 130}},
				[BloodPressureSubConfigKey.DIASTOLIC]: {measurement: "mmHg", defaultRange: {min: 60, max: 80}}
			},
			default: DefaultEveryDay,
			show: true,
			toggle: true
		},
		[HealthMeasurementsConfigKey.WEIGHT]: {
			key: HealthMeasurementsConfigKey.WEIGHT,
			presentation: "Weight",
			shortDescription: ["ElliQ will ask the customer for their weight"],
			type: "frequency",
			options: [
				DefaultEveryDay,
				DefaultEveryWeek,
				DefaultEveryMonth
			],
			units: [],
			subKeys: {
				[HealthMeasurementsConfigKey.WEIGHT]: {measurement: "LBS"}
			},
			default: DefaultEveryWeek,
			show: true,
			toggle: true
		},
		[HealthMeasurementsConfigKey.BLOOD_GLUCOSE]: {
			key: HealthMeasurementsConfigKey.BLOOD_GLUCOSE,
			presentation: "Blood glucose",
			shortDescription: ["ElliQ will ask the customer for their blood glucose measurement"],
			type: "frequency",
			options: [
				DefaultEveryDay,
				DefaultTwiceDay,
				DefaultTriceDay
			],
			units: [],
			subKeys: {
				[BloodGlucoseSubConfigKey.BEFORE_BREAKFAST]: {measurement: "mg/dL", defaultRange: {min: 100, max: 105}},
				[BloodGlucoseSubConfigKey.AFTER_BREAKFAST]: {measurement: "mg/dL", defaultRange: {min: 90, max: 130}},
				[BloodGlucoseSubConfigKey.BEFORE_LUNCH]: {measurement: "mg/dL", defaultRange: {min: 100, max: 105}},
				[BloodGlucoseSubConfigKey.AFTER_LUNCH]: {measurement: "mg/dL", defaultRange: {min: 90, max: 130}},
				[BloodGlucoseSubConfigKey.BEFORE_DINNER]: {measurement: "mg/dL", defaultRange: {min: 100, max: 105}},
				[BloodGlucoseSubConfigKey.AFTER_DINNER]: {measurement: "mg/dL", defaultRange: {min: 90, max: 130}}
			},
			default: DefaultEveryDay,
			show: true,
			toggle: true
		},
		[HealthMeasurementsConfigKey.BLOOD_OXYGEN]: {
			key: HealthMeasurementsConfigKey.BLOOD_OXYGEN,
			presentation: "Blood oxygen saturation",
			shortDescription: ["ElliQ will ask the customer for their blood oxygen saturation"],
			type: "frequency",
			options: [
				DefaultEveryDay,
				DefaultEveryWeek,
				DefaultTwiceAWeek
			],
			units: [],
			subKeys: {
				[HealthMeasurementsConfigKey.BLOOD_OXYGEN]: {measurement: "%", defaultRange: {min: 95, max: 100}}
			},
			default: DefaultEveryDay,
			show: true,
			toggle: true
		},
		[HealthMeasurementsConfigKey.HEART_RATE]: {
			key: HealthMeasurementsConfigKey.HEART_RATE,
			presentation: "Heart rate",
			shortDescription: ["ElliQ will ask the customer for their heart rate"],
			type: "frequency",
			options: [
				DefaultEveryDay,
				DefaultEveryWeek,
				DefaultTwiceAWeek
			],
			units: [],
			subKeys: {
				[HealthMeasurementsConfigKey.HEART_RATE]: {measurement: "bpm", defaultRange: {min: 60, max: 100}}
			},
			default: DefaultEveryDay,
			show: true,
			toggle: true
		}
	};
};

export const measurementFieldDefsArrayForOrganization = (): MeasurementFieldDef[] => {
	return Object.values(measurementFieldDefsForOrganization());
};

type WellnessCoachFieldDef = CareFieldDef & { key: WellnessCoachConfigKey };

type WellnessCoachFieldDefs = Record<WellnessCoachConfigKey, WellnessCoachFieldDef>;

export const StaticWellnessCoachFieldDefs: WellnessCoachFieldDefs = {
	[WellnessCoachConfigKey.INCREASE_PHYSICAL_ACTIVITY]: {
		key: WellnessCoachConfigKey.INCREASE_PHYSICAL_ACTIVITY,
		presentation: "Increasing physical activity",
		shortDescription: ["Goal triggers the following ElliQ plans:",
		                   "\u2022 Physical Exercise"],
		type: "frequency",
		options: [],
		units: [],
		default: DefaultEveryDay,
		show: true,
		toggle: true
	},
	[WellnessCoachConfigKey.STAY_SHARP]: {
		key: WellnessCoachConfigKey.STAY_SHARP,
		presentation: "Staying sharp",
		shortDescription: ["Goal triggers the following ElliQ plans:",
		                   "\u2022 Trivia Quiz",
		                   "\u2022 Flag Quiz",
		                   "\u2022 Word Scramble"],
		type: "frequency",
		options: [],
		units: [],
		default: DefaultEveryDay,
		show: true,
		toggle: true
	},
	[WellnessCoachConfigKey.INCREASE_RELAXATION]: {
		key: WellnessCoachConfigKey.INCREASE_RELAXATION,
		presentation: "Increasing relaxation",
		shortDescription: ["Goal triggers the following ElliQ plans:",
		                   "\u2022 Mindfulness",
		                   "\u2022 Breathing Exercises",
		                   "\u2022 Smile Exercise",
		                   "\u2022 Gratitude Exercise",
		                   "\u2022 Stress Reduction"],
		type: "frequency",
		options: [],
		units: [],
		default: DefaultEveryDay,
		show: true,
		toggle: true
	},
	[WellnessCoachConfigKey.IMPROVE_SLEEP]: {
		key: WellnessCoachConfigKey.IMPROVE_SLEEP,
		presentation: "Improving sleep quality",
		shortDescription: ["Goal triggers the following ElliQ plans:",
		                   "\u2022 Sleep Better Exercise",
		                   "\u2022 Breathing Exercises",
		                   "\u2022 Mindfulness Exercises",
		                   "\u2022 Stress Reduction"],
		type: "frequency",
		options: [],
		units: [],
		default: DefaultEveryDay,
		show: true,
		toggle: true
	},
	[WellnessCoachConfigKey.HYDRATION_REMINDER]: {
		key: WellnessCoachConfigKey.HYDRATION_REMINDER,
		presentation: "Hydration Reminder",
		shortDescription: ["Goal triggers the following ElliQ plans:",
		                   "\u2022 Water Reminder"],
		type: "frequency",
		options: [],
		units: [],
		default: DefaultFiveTimesDay,
		show: true,
		toggle: true,
		timeUnits: ["day"]
	}
};

export const wellnessCoachFieldDefsForOrganization = (organization?: DB_Organization): WellnessCoachFieldDefs => {
	return StaticWellnessCoachFieldDefs;
};

export const wellnessCoachFieldDefsArrayForOrganization = (organization?: DB_Organization): WellnessCoachFieldDef[] => {
	return Object.values(wellnessCoachFieldDefsForOrganization(organization));
};


export const getInitialConfigForOrganization = (organization?: DB_Organization): CareConfig => {
	const checkinFieldDefs = checkinFieldDefsForOrganization(organization);
	const measurementFieldDefs = measurementFieldDefsForOrganization();
	const wellnessCoachFieldDefs = wellnessCoachFieldDefsForOrganization(organization);
	// Create the base configuration (all disabled) based on the frequency in the field definitions.
	const baseConfig: { [p: string]: CareFieldVal } = {
		...(checkinElements.reduce((acc, key) => {
			return {
				...acc,
				[key]: frequencyCareElement(checkinFieldDefs[key as CheckinConfigKey].default)
			};
		}, {})),
		...(measurementsElements.reduce((acc, key) => {
			return {
				...acc,
				[key]: frequencyCareElement(measurementFieldDefs[key as HealthMeasurementsConfigKey].default)
			};
		}, {})),
		...(wellnessCoachElements.reduce((acc, key) => {
			return {
				...acc,
				[key]: {...frequencyCareElement(wellnessCoachFieldDefs[key as WellnessCoachConfigKey].default)}
			};
		}, {}))
	};
	// the general_wellbeing key should be enabled by default for clinical users
	if (organization?.type === ORGANIZATION_TYPE.CLINICAL) {
		return {
			...baseConfig,
			[CheckinConfigKey.ENERGY_CHECK]: {...baseConfig[CheckinConfigKey.ENERGY_CHECK], enabled: true},
			[CheckinConfigKey.PAIN_ASSESSMENT]: {...baseConfig[CheckinConfigKey.PAIN_ASSESSMENT], enabled: true},
			[CheckinConfigKey.GENERAL_WELLBEING]: {...baseConfig[CheckinConfigKey.GENERAL_WELLBEING], enabled: true}
		};
	}
	return baseConfig;
};

export const getRangeByTimeUnitForKey = (timeUnit: TimeUnit, key: string) => {
	switch (timeUnit) {
		case "day":
			if (key === WellnessCoachConfigKey.HYDRATION_REMINDER)
				return {start: 0, end: 6};
			if (wellnessCoachElements.includes(key))
				return {start: 1, end: 7};
			return {start: 1, end: 4};
		case "week":
			return {start: 1, end: 7};
		case "month":
			return {start: 1, end: 30};
		default:
			return {start: 1, end: 2};
	}
};
